import React, {ReactNode, useContext, useEffect} from 'react';

import {PDPProduct} from "../../model/BonusPointsProduct";
import {AccordionGroup, AccordionItem, Tabs, TabsConfig} from "@zooplus/ui-components";
import LocResourcesContext from "../../../common/contexts/LocResourcesContext";
import ReviewsTab from "./tabs/ReviewsTab";
import AnalyticalConstituentsTab from "./tabs/AnalyticalConstituentsTab";

interface PDPProductDetailsComponentProps {
  product: PDPProduct;
  mediaServerUrl: string;
  onRenderSuccess: () => void;
}

const PDPProductDetails: React.FC<PDPProductDetailsComponentProps> = ({
  product,
  mediaServerUrl,
  onRenderSuccess
}: PDPProductDetailsComponentProps) => {
  const GLOBAL_PREFIX= 'loy-bp-pdppd';
  const {onFormatLocale} = useContext(LocResourcesContext);

  function simpleTab(content: String, isMobile: boolean): ReactNode {
    const className = isMobile
      ? `${GLOBAL_PREFIX}_accordion-item-content`
      : `${GLOBAL_PREFIX}_tab-content`;
    return <div dangerouslySetInnerHTML={{__html: content}} className={className} />
  }

  function getTabsConfig(): TabsConfig[] {
    const tabs: TabsConfig[] = [];
    tabs.push({
      label: onFormatLocale!('productDetail.tabs.title.description'),
      content: simpleTab(product.description, false)
    });
    if (product.ingredients?.length) {
      tabs.push({
        label: onFormatLocale!('productDetail.tabs.title.ingredients'),
        content: simpleTab(product.ingredients, false)
      });
    }
    if (product.analyticalConstituents.length) {
      tabs.push({
        label: onFormatLocale!('productDetail.tabs.title.analyticalconstituents'),
        content: <AnalyticalConstituentsTab isMobile={false} product={product} />
      });
    }
    if (product.feedingRecommendations?.length) {
      tabs.push({
        label: onFormatLocale!('productDetail.tabs.title.feedingrecommendation'),
        content: simpleTab(product.feedingRecommendations, false)
      });
    }
    if (product.feedback.length) {
      tabs.push({
        label: onFormatLocale!('productDetail.tabs.title.reviews'),
        content: <ReviewsTab isMobile={false} product={product} mediaServerUrl={mediaServerUrl} />
      });
    }
    return tabs;
  }

  function getAccordionItems(): ReactNode[] {
    const items: ReactNode[] = []
    const item = (titleKey: string, content: ReactNode): ReactNode =>
      <AccordionItem title={onFormatLocale!(titleKey)}>
        <div className={`${GLOBAL_PREFIX}_accordion-item`}>{content}</div>
      </AccordionItem>

    items.push(item(
      'productDetail.tabs.title.description',
      simpleTab(product.description, true)
    ));
    if (product.ingredients?.length) {
      items.push(item(
        'productDetail.tabs.title.ingredients',
        simpleTab(product.ingredients, true)
      ));
    }
    if (product.analyticalConstituents.length) {
      items.push(item(
        'productDetail.tabs.title.analyticalconstituents',
        <AnalyticalConstituentsTab isMobile={true} product={product} />
      ));
    }
    if (product.feedingRecommendations?.length) {
      items.push(item(
        'productDetail.tabs.title.feedingrecommendation',
        simpleTab(product.feedingRecommendations, true)
      ));
    }
    if (product.feedback.length) {
      items.push(item(
        'productDetail.tabs.title.reviews',
        <ReviewsTab isMobile={true} product={product} mediaServerUrl={mediaServerUrl} />
      ));
    }
    return items
  }

  useEffect(() => {if (onFormatLocale) onRenderSuccess();}, [onFormatLocale]);

  return <>
    <div id={'pdp-product-details'} className={`${GLOBAL_PREFIX}_desktop-wrapper`}>
      <Tabs id={'pdp-product-info'} tabsConfig={getTabsConfig()}/>
    </div>
    <div id={'pdp-product-details'} className={`${GLOBAL_PREFIX}_mobile-wrapper`}>
      <AccordionGroup>{getAccordionItems()}</AccordionGroup>
    </div>
  </>;
};

export default React.memo(PDPProductDetails);
